// Drag-to-rotate viewer for ambient PDP turntable videos.
//
// Wraps the existing autoplay/loop/muted product video and maps horizontal
// pointer drags onto the video timeline: one full drag across the viewer
// equals one full rotation (one loop of the clip). While idle the video
// keeps its native ambient looping behavior; after a drag it resumes
// playing following a short pause.
//
// Keyboard: the wrapper is focusable and Left/Right arrows rotate in steps.

if (!customElements.get('video-spin-viewer')) {
  customElements.define(
    'video-spin-viewer',
    class VideoSpinViewer extends HTMLElement {
      static RESUME_DELAY_MS = 2000;
      static KEY_STEP_FRACTION = 1 / 24; // one arrow press = 15 degrees

      connectedCallback() {
        // The gallery re-parents media items on variant changes (prepend/
        // insertBefore), which re-runs connectedCallback on the same element.
        // Guard so listeners are only ever attached once.
        if (this.initialized) return;

        this.video = this.querySelector('video');
        if (!this.video) return;

        try {
          if (navigator.audioSession) navigator.audioSession.type = 'ambient';
        } catch (error) {}
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.volume = 0;
        this.video.playsInline = true;

        this.initialized = true;
        this.dragging = false;
        this.pendingTime = null;
        this.rafId = null;
        this.resumeTimer = null;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerEnd = this.onPointerEnd.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerup', this.onPointerEnd);
        this.addEventListener('pointercancel', this.onPointerEnd);
        this.addEventListener('keydown', this.onKeyDown);

        this.updateAriaValue();
      }

      disconnectedCallback() {
        clearTimeout(this.resumeTimer);
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      get duration() {
        const d = this.video?.duration;
        return Number.isFinite(d) && d > 0 ? d : null;
      }

      onPointerDown(event) {
        if (event.button !== 0 && event.pointerType === 'mouse') return;
        if (!this.duration) {
          // Metadata not ready yet; make sure it loads for the next attempt.
          this.video.preload = 'auto';
          this.video.load?.();
          return;
        }

        // Buffer the whole (small) clip so scrubbing is smooth.
        if (this.video.preload !== 'auto') this.video.preload = 'auto';

        this.dragging = true;
        this.startX = event.clientX;
        this.startTime = this.video.currentTime;
        this.classList.add('is-dragging');
        this.toggleAttribute('data-interacted', true);
        clearTimeout(this.resumeTimer);
        this.video.pause();
        this.setPointerCapture(event.pointerId);
      }

      onPointerMove(event) {
        if (!this.dragging || !this.duration) return;

        // Negative mapping so the ring face follows the cursor: dragging right
        // rotates the side of the ring facing you to the right.
        const width = this.getBoundingClientRect().width || 1;
        const deltaX = event.clientX - this.startX;
        const deltaTime = (-deltaX / width) * this.duration;
        const wrapped =
          ((this.startTime + deltaTime) % this.duration + this.duration) % this.duration;

        this.scheduleSeek(wrapped);
      }

      onPointerEnd(event) {
        if (!this.dragging) return;
        this.dragging = false;
        this.classList.remove('is-dragging');
        if (this.hasPointerCapture?.(event.pointerId)) this.releasePointerCapture(event.pointerId);
        this.scheduleResume();
      }

      onKeyDown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (!this.duration) return;
        event.preventDefault();

        this.toggleAttribute('data-interacted', true);
        clearTimeout(this.resumeTimer);
        this.video.pause();

        // Matches the drag mapping: ArrowRight rotates the same way as dragging right.
        const direction = event.key === 'ArrowRight' ? -1 : 1;
        const step = this.duration * VideoSpinViewer.KEY_STEP_FRACTION * direction;
        const wrapped =
          ((this.video.currentTime + step) % this.duration + this.duration) % this.duration;

        this.scheduleSeek(wrapped);
        this.scheduleResume();
      }

      // Coalesce seeks to one per animation frame and never queue a new seek
      // while the previous one is still decoding.
      scheduleSeek(time) {
        this.pendingTime = time;
        if (this.rafId) return;

        const apply = () => {
          this.rafId = null;
          if (this.pendingTime === null) return;
          if (this.video.seeking) {
            this.rafId = requestAnimationFrame(apply);
            return;
          }
          this.video.currentTime = this.pendingTime;
          this.pendingTime = null;
          this.updateAriaValue();
        };

        this.rafId = requestAnimationFrame(apply);
      }

      // Return to the theme's ambient looping playback after a short pause.
      scheduleResume() {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => {
          if (this.dragging) return;
          const playPromise = this.video.play();
          if (playPromise) playPromise.catch(() => {});
        }, VideoSpinViewer.RESUME_DELAY_MS);
      }

      updateAriaValue() {
        if (!this.duration) return;
        const degrees = Math.round((this.video.currentTime / this.duration) * 360) % 360;
        this.setAttribute('aria-valuenow', degrees);
      }
    }
  );
}
